import React, { useRef } from 'react';
import { View, StyleSheet, ScrollView, Platform, LayoutChangeEvent, Dimensions, TouchableOpacity } from 'react-native';
import { LazyImage } from '@/components/common/admin/mobile/LazyImage';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

const { width: screenWidth } = Dimensions.get('window');

interface BulletinImagesCarouselProps {
  files: Array<{ id?: number; fileUrl?: string }>; 
  computeHeight: (effectiveWidth: number) => number;
  maxImageHeight: number;
}

export const BulletinImagesCarousel: React.FC<BulletinImagesCarouselProps> = ({ files, computeHeight, maxImageHeight }) => {
  const imageCount = files?.length || 0;
  const scrollViewRef = useRef<ScrollView | null>(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [containerWidth, setContainerWidth] = React.useState<number>(0);

  const onLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  const scrollTo = (index: number) => {
    if (!scrollViewRef.current) return;
    const frameW = containerWidth || (screenWidth - 64);
    scrollViewRef.current.scrollTo({ x: index * frameW, animated: true });
    setCurrentIndex(index);
  };

  const onPrev = () => currentIndex > 0 && scrollTo(currentIndex - 1);
  const onNext = () => currentIndex < imageCount - 1 && scrollTo(currentIndex + 1);

  if (!imageCount) return null;

  return (
    <View style={styles.imagesContainer} onLayout={onLayout}>
      <View style={styles.imagesWrapper}>
        <ScrollView
          ref={scrollViewRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.imagesScrollContent}
          pagingEnabled={imageCount === 1}
          decelerationRate={imageCount > 1 ? 'fast' as any : undefined}
          snapToAlignment={imageCount > 1 ? 'start' : undefined}
          snapToInterval={imageCount > 1 ? (containerWidth || (screenWidth - 64)) : undefined}
          disableIntervalMomentum={imageCount > 1 ? true : undefined}
          onMomentumScrollEnd={(event) => {
            const frameW = (containerWidth || (screenWidth - 64));
            const newIdx = Math.round(event.nativeEvent.contentOffset.x / frameW);
            setCurrentIndex(newIdx);
          }}
        >
          {files.map((file, index) => {
            const frameW = (containerWidth || (screenWidth - 64));
            const imageHeight = Platform.OS === 'web' ? computeHeight(frameW) : maxImageHeight;
            return (
              <View key={file?.id || index} style={[styles.imageFrame, { width: frameW, height: imageHeight }] }>
                <LazyImage source={{ uri: file?.fileUrl || '' }} style={{ width: '100%', height: imageHeight }} resizeMode={Platform.OS === 'web' ? 'contain' : 'cover'} />
              </View>
            );
          })}
        </ScrollView>
        {imageCount > 1 && (
          <>
            {currentIndex > 0 && (
              <TouchableOpacity style={[styles.navButton, styles.navLeft]} onPress={onPrev} activeOpacity={0.7}>
                <SVG_COMPONENTS.CHEVRON_LEFT width={20} height={20} color={ADMIN_COLORS.TEXT_PRIMARY} />
              </TouchableOpacity>
            )}
            {currentIndex < imageCount - 1 && (
              <TouchableOpacity style={[styles.navButton, styles.navRight]} onPress={onNext} activeOpacity={0.7}>
                <SVG_COMPONENTS.CHEVRON_RIGHT width={20} height={20} color={ADMIN_COLORS.TEXT_PRIMARY} />
              </TouchableOpacity>
            )}
          </>
        )}
      </View>
      {imageCount > 1 && (
        <View style={styles.imageIndicators}>
          {files.map((_, idx) => (
            <View key={idx} style={[styles.imageIndicatorDot, idx === currentIndex && styles.imageIndicatorDotActive]} />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  imagesContainer: { marginBottom: 12 },
  imagesWrapper: { position: 'relative' },
  imagesScrollContent: { paddingRight: 0, alignItems: 'center' },
  imageFrame: { borderRadius: 8, overflow: 'hidden', backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY },
  navButton: { position: 'absolute', top: '50%', transform: [{ translateY: -20 }], borderRadius: 20, width: 40, height: 40, justifyContent: 'center', alignItems: 'center', elevation: 5 },
  navLeft: { left: 8 },
  navRight: { right: 8 },
  imageIndicators: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 8, gap: 6 },
  imageIndicatorDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: 'rgba(255, 255, 255, 0.4)' },
  imageIndicatorDotActive: { backgroundColor: ADMIN_COLORS.PRIMARY, width: 8, height: 8, borderRadius: 4 },
});

export default BulletinImagesCarousel;


